/**
 * Frontend Block Configuration Type System
 *
 * This file mirrors the backend C# configuration classes exactly.
 * All property names use camelCase (JSON serialization of PascalCase C# properties).
 */

// =============================================================================
// COMMON CONFIGURATION INTERFACES
// =============================================================================

export interface PaddingConfiguration {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface BorderConfiguration {
  width: number;
  style: 'solid' | 'dashed' | 'dotted' | 'none';
  color: string;
  radius: number;
}

export interface TypographyConfiguration {
  fontSize: number;
  fontFamily: 'Arial' | 'Georgia' | 'Helvetica' | 'Times New Roman' | 'Verdana' | 'Courier New';
  fontWeight: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
  lineHeight: number;
  color: string;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing: number;
}

export interface AlignmentConfiguration {
  horizontal: 'left' | 'center' | 'right' | 'justify';
  vertical: 'top' | 'middle' | 'bottom';
}

export interface BackgroundConfiguration {
  color?: string;
  imageUrl?: string;
  imageSize?: 'cover' | 'contain' | 'auto';
  imageRepeat?: 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y';
}

export interface LinkConfiguration {
  enabled: boolean;
  url?: string;
  openInNewTab: boolean;
  title?: string;
}

export interface ResponsiveConfiguration {
  hideOnMobile: boolean;
  hideOnDesktop: boolean;
}

// =============================================================================
// NAMED SUB-ITEM INTERFACES
// =============================================================================

export interface SocialLink {
  platform: 'facebook' | 'twitter' | 'instagram' | 'linkedin' | 'youtube' | 'pinterest' | 'tiktok' | 'github';
  url: string;
  label?: string;
  customIconUrl?: string;
}

export interface FooterLink {
  text: string;
  url: string;
}

export interface MenuItem {
  text: string;
  href: string;
  title?: string;
}

export interface AccordionItem {
  header: string;
  content: string;
  isOpen?: boolean;
}

export interface CarouselSlide {
  imageUrl: string;
  alt: string;
  caption?: string;
  linkUrl?: string;
  linkText?: string;
}

// =============================================================================
// DEFAULT CONFIGURATION VALUES
// =============================================================================

export const DEFAULT_TYPOGRAPHY: TypographyConfiguration = {
  fontSize: 14,
  fontFamily: 'Arial',
  fontWeight: 'normal',
  lineHeight: 1.5,
  color: '#000000',
  italic: false,
  underline: false,
  strikethrough: false,
  textTransform: 'none',
  letterSpacing: 0,
};

export const DEFAULT_ALIGNMENT: AlignmentConfiguration = {
  horizontal: 'left',
  vertical: 'top',
};

export const DEFAULT_PADDING: PaddingConfiguration = {
  top: 10,
  right: 10,
  bottom: 10,
  left: 10,
};

export const DEFAULT_BACKGROUND: BackgroundConfiguration = {
  color: undefined,
  imageUrl: undefined,
  imageSize: 'cover',
  imageRepeat: 'no-repeat',
};

export const DEFAULT_BORDER: BorderConfiguration = {
  width: 0,
  style: 'solid',
  color: '#000000',
  radius: 0,
};

export const DEFAULT_LINK: LinkConfiguration = {
  enabled: false,
  url: undefined,
  openInNewTab: true,
  title: undefined,
};

export const DEFAULT_RESPONSIVE: ResponsiveConfiguration = {
  hideOnMobile: false,
  hideOnDesktop: false,
};

// =============================================================================
// HELPER FUNCTIONS FOR CREATING DEFAULT CONFIGURATIONS
// =============================================================================

export function createDefaultTypography(overrides?: Partial<TypographyConfiguration>): TypographyConfiguration {
  return { ...DEFAULT_TYPOGRAPHY, ...overrides };
}

export function createDefaultAlignment(overrides?: Partial<AlignmentConfiguration>): AlignmentConfiguration {
  return { ...DEFAULT_ALIGNMENT, ...overrides };
}

export function createDefaultPadding(overrides?: Partial<PaddingConfiguration>): PaddingConfiguration {
  return { ...DEFAULT_PADDING, ...overrides };
}

export function createDefaultBackground(overrides?: Partial<BackgroundConfiguration>): BackgroundConfiguration {
  return { ...DEFAULT_BACKGROUND, ...overrides };
}

export function createDefaultBorder(overrides?: Partial<BorderConfiguration>): BorderConfiguration {
  return { ...DEFAULT_BORDER, ...overrides };
}

export function createDefaultLink(overrides?: Partial<LinkConfiguration>): LinkConfiguration {
  return { ...DEFAULT_LINK, ...overrides };
}

export function createDefaultResponsive(overrides?: Partial<ResponsiveConfiguration>): ResponsiveConfiguration {
  return { ...DEFAULT_RESPONSIVE, ...overrides };
}

// =============================================================================
// BASE BLOCK CONFIGURATION
// =============================================================================

export interface BaseBlockConfiguration {
  $blockType: string;
}

// =============================================================================
// BLOCK-SPECIFIC CONFIGURATION INTERFACES
// =============================================================================

export interface TextBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'text';
  text: string;
  typography: TypographyConfiguration;
  alignment: AlignmentConfiguration;
  padding: PaddingConfiguration;
  link?: LinkConfiguration;
  responsive: ResponsiveConfiguration;
}

export interface HeadingBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'heading';
  text: string;
  level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  typography: TypographyConfiguration;
  alignment: AlignmentConfiguration;
  padding: PaddingConfiguration;
  link?: LinkConfiguration;
  responsive: ResponsiveConfiguration;
}

export interface ButtonBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'button';
  text: string;
  href: string;
  typography: TypographyConfiguration;
  alignment: AlignmentConfiguration;
  padding: PaddingConfiguration;
  background: BackgroundConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  width: 'auto' | 'full';
  minWidth?: number;
  maxWidth?: number;
}

export interface ImageBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'image';
  src: string;
  alt: string;
  alignment: AlignmentConfiguration;
  border: BorderConfiguration;
  padding: PaddingConfiguration;
  link?: LinkConfiguration;
  responsive: ResponsiveConfiguration;
  width?: number;
  height?: number;
  title?: string;
  fitMode: 'auto' | 'cover' | 'contain';
  mobileSrc?: string;
  mobileWidth?: number;
  fluidOnMobile?: boolean;
}

export interface VideoBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'video';
  src: string;
  thumbnailUrl: string;
  alt: string;
  alignment: AlignmentConfiguration;
  border: BorderConfiguration;
  padding: PaddingConfiguration;
  responsive: ResponsiveConfiguration;
  width: number;
  height?: number;
  title?: string;
  autoPlay: boolean;
  showControls: boolean;
  loop: boolean;
  muted: boolean;
  provider: 'youtube' | 'vimeo' | 'native';
}

export interface DividerBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'divider';
  border: BorderConfiguration;
  padding: PaddingConfiguration;
  alignment: AlignmentConfiguration;
  responsive: ResponsiveConfiguration;
  width: string;
  containerWidth?: number;
  cssClass?: string;
}

export interface SpacerBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'spacer';
  height: number;
  mobileHeight?: number;
  backgroundColor: string;
  containerWidth?: number;
  cssClass?: string;
  responsive: ResponsiveConfiguration;
}

export interface SocialBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'social';
  links: SocialLink[];
  alignment: AlignmentConfiguration;
  padding: PaddingConfiguration;
  responsive: ResponsiveConfiguration;
  layout: 'horizontal' | 'vertical';
  iconSize: number;
  iconSpacing: number;
  iconShape: 'circle' | 'square' | 'rounded';
  iconStyle: 'color' | 'monochrome' | 'custom';
  border: BorderConfiguration;
}

export interface QuoteBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'quote';
  text: string;
  author?: string;
  source?: string;
  typography: TypographyConfiguration;
  alignment: AlignmentConfiguration;
  padding: PaddingConfiguration;
  background: BackgroundConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  quoteMarks: 'none' | 'single' | 'double' | 'guillemet';
  sourceUrl?: string;
}

export interface MenuBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'menu';
  items: MenuItem[];
  typography: TypographyConfiguration;
  alignment: AlignmentConfiguration;
  padding: PaddingConfiguration;
  responsive: ResponsiveConfiguration;
  layout: 'horizontal' | 'vertical';
  itemSpacing: number;
  separator?: string;
  hoverColor: string;
}

export interface SectionBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'section';
  background: BackgroundConfiguration;
  padding: PaddingConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  direction: 'ltr' | 'rtl';
  verticalAlign: 'stretch' | 'top' | 'middle' | 'bottom';
  maxWidth?: number;
  fullWidth: boolean;
  cssClass?: string;
}

export interface ColumnBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'column';
  background: BackgroundConfiguration;
  padding: PaddingConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  width: string;
  verticalAlign: 'top' | 'middle' | 'bottom';
  direction: 'ltr' | 'rtl';
  cssClass?: string;
  mobileStack: boolean;
  mobileStackOrder: number;
}

export interface GroupBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'group';
  background: BackgroundConfiguration;
  padding: PaddingConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  direction: 'ltr' | 'rtl';
  layout: 'horizontal' | 'vertical';
  gap: number;
  itemsAlign: 'stretch' | 'start' | 'center' | 'end';
  cssClass?: string;
}

export interface HeroBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'hero';
  title?: string;
  subtitle?: string;
  background: BackgroundConfiguration;
  padding: PaddingConfiguration;
  alignment: AlignmentConfiguration;
  titleTypography: TypographyConfiguration;
  subtitleTypography: TypographyConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  minHeight?: number;
  verticalAlign: 'top' | 'middle' | 'bottom';
  ctaText?: string;
  ctaUrl?: string;
  ctaBackgroundColor?: string;
  ctaTextColor?: string;
  overlayColor?: string;
  overlayOpacity?: number;
}

export interface FooterBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'footer';
  companyName?: string;
  address?: string;
  legalText?: string;
  unsubscribeUrl?: string;
  background: BackgroundConfiguration;
  padding: PaddingConfiguration;
  alignment: AlignmentConfiguration;
  typography: TypographyConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  links?: FooterLink[];
  socialLinks?: SocialLink[];
  showUnsubscribe: boolean;
  cssClass?: string;
}

export interface HtmlBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'html';
  content: string;
  responsive: ResponsiveConfiguration;
}

export interface AccordionBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'accordion';
  items: AccordionItem[];
  padding: PaddingConfiguration;
  background: BackgroundConfiguration;
  border: BorderConfiguration;
  headerTypography: TypographyConfiguration;
  contentTypography: TypographyConfiguration;
  responsive: ResponsiveConfiguration;
  allowMultipleOpen: boolean;
  defaultOpenIndex: number;
  headerBackgroundColor: string;
  headerActiveColor: string;
  cssClass?: string;
}

export interface CarouselBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'carousel';
  slides: CarouselSlide[];
  padding: PaddingConfiguration;
  background: BackgroundConfiguration;
  border: BorderConfiguration;
  responsive: ResponsiveConfiguration;
  width: number;
  height: number;
  autoPlay: boolean;
  autoPlayInterval: number;
  showControls: boolean;
  showIndicators: boolean;
  loop: boolean;
  transition: 'fade' | 'slide';
  cssClass?: string;
}

export interface CustomBlockConfiguration extends BaseBlockConfiguration {
  $blockType: 'custom';
  customBlockType: string;
  data: Record<string, unknown>;
}

// =============================================================================
// UNION TYPE FOR ALL BLOCK CONFIGURATIONS
// =============================================================================

export type BlockConfiguration =
  | TextBlockConfiguration
  | HeadingBlockConfiguration
  | ButtonBlockConfiguration
  | ImageBlockConfiguration
  | VideoBlockConfiguration
  | DividerBlockConfiguration
  | SpacerBlockConfiguration
  | SocialBlockConfiguration
  | QuoteBlockConfiguration
  | MenuBlockConfiguration
  | SectionBlockConfiguration
  | ColumnBlockConfiguration
  | GroupBlockConfiguration
  | HeroBlockConfiguration
  | FooterBlockConfiguration
  | HtmlBlockConfiguration
  | AccordionBlockConfiguration
  | CarouselBlockConfiguration
  | CustomBlockConfiguration;

// =============================================================================
// TYPE GUARD FUNCTIONS
// =============================================================================

export function isTextBlockConfiguration(config: BlockConfiguration): config is TextBlockConfiguration {
  return config.$blockType === 'text';
}

export function isHeadingBlockConfiguration(config: BlockConfiguration): config is HeadingBlockConfiguration {
  return config.$blockType === 'heading';
}

export function isButtonBlockConfiguration(config: BlockConfiguration): config is ButtonBlockConfiguration {
  return config.$blockType === 'button';
}

export function isImageBlockConfiguration(config: BlockConfiguration): config is ImageBlockConfiguration {
  return config.$blockType === 'image';
}

export function isVideoBlockConfiguration(config: BlockConfiguration): config is VideoBlockConfiguration {
  return config.$blockType === 'video';
}

export function isDividerBlockConfiguration(config: BlockConfiguration): config is DividerBlockConfiguration {
  return config.$blockType === 'divider';
}

export function isSpacerBlockConfiguration(config: BlockConfiguration): config is SpacerBlockConfiguration {
  return config.$blockType === 'spacer';
}

export function isSocialBlockConfiguration(config: BlockConfiguration): config is SocialBlockConfiguration {
  return config.$blockType === 'social';
}

export function isQuoteBlockConfiguration(config: BlockConfiguration): config is QuoteBlockConfiguration {
  return config.$blockType === 'quote';
}

export function isMenuBlockConfiguration(config: BlockConfiguration): config is MenuBlockConfiguration {
  return config.$blockType === 'menu';
}

export function isSectionBlockConfiguration(config: BlockConfiguration): config is SectionBlockConfiguration {
  return config.$blockType === 'section';
}

export function isColumnBlockConfiguration(config: BlockConfiguration): config is ColumnBlockConfiguration {
  return config.$blockType === 'column';
}

export function isGroupBlockConfiguration(config: BlockConfiguration): config is GroupBlockConfiguration {
  return config.$blockType === 'group';
}

export function isHeroBlockConfiguration(config: BlockConfiguration): config is HeroBlockConfiguration {
  return config.$blockType === 'hero';
}

export function isFooterBlockConfiguration(config: BlockConfiguration): config is FooterBlockConfiguration {
  return config.$blockType === 'footer';
}

export function isHtmlBlockConfiguration(config: BlockConfiguration): config is HtmlBlockConfiguration {
  return config.$blockType === 'html';
}

export function isAccordionBlockConfiguration(config: BlockConfiguration): config is AccordionBlockConfiguration {
  return config.$blockType === 'accordion';
}

export function isCarouselBlockConfiguration(config: BlockConfiguration): config is CarouselBlockConfiguration {
  return config.$blockType === 'carousel';
}

export function isCustomBlockConfiguration(config: BlockConfiguration): config is CustomBlockConfiguration {
  return config.$blockType === 'custom';
}

// =============================================================================
// BLOCK-SPECIFIC DEFAULT CONFIGURATION CREATORS
// =============================================================================

export function createDefaultTextBlockConfiguration(overrides?: Partial<TextBlockConfiguration>): TextBlockConfiguration {
  return {
    $blockType: 'text',
    text: 'Enter your text here...',
    typography: createDefaultTypography(),
    alignment: createDefaultAlignment(),
    padding: createDefaultPadding(),
    link: undefined,
    responsive: createDefaultResponsive(),
    ...overrides,
  };
}

export function createDefaultHeadingBlockConfiguration(overrides?: Partial<HeadingBlockConfiguration>): HeadingBlockConfiguration {
  return {
    $blockType: 'heading',
    text: 'Heading Text',
    level: 'h2',
    typography: createDefaultTypography({ fontSize: 24, fontWeight: 'bold' }),
    alignment: createDefaultAlignment(),
    padding: createDefaultPadding(),
    link: undefined,
    responsive: createDefaultResponsive(),
    ...overrides,
  };
}

export function createDefaultButtonBlockConfiguration(overrides?: Partial<ButtonBlockConfiguration>): ButtonBlockConfiguration {
  return {
    $blockType: 'button',
    text: 'Click Here',
    href: 'https://example.com',
    typography: createDefaultTypography({ fontSize: 16, fontWeight: 'bold', color: '#ffffff' }),
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    padding: createDefaultPadding({ top: 12, right: 24, bottom: 12, left: 24 }),
    background: createDefaultBackground({ color: '#007bff' }),
    border: createDefaultBorder({ radius: 4 }),
    responsive: createDefaultResponsive(),
    width: 'auto',
    minWidth: undefined,
    maxWidth: undefined,
    ...overrides,
  };
}

export function createDefaultImageBlockConfiguration(overrides?: Partial<ImageBlockConfiguration>): ImageBlockConfiguration {
  return {
    $blockType: 'image',
    src: 'https://via.placeholder.com/600x400',
    alt: 'Image description',
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    border: createDefaultBorder(),
    padding: createDefaultPadding({ top: 0, right: 0, bottom: 10, left: 0 }),
    link: undefined,
    responsive: createDefaultResponsive(),
    width: 600,
    height: undefined,
    title: undefined,
    fitMode: 'auto',
    mobileSrc: undefined,
    mobileWidth: undefined,
    fluidOnMobile: false,
    ...overrides,
  };
}

export function createDefaultVideoBlockConfiguration(overrides?: Partial<VideoBlockConfiguration>): VideoBlockConfiguration {
  return {
    $blockType: 'video',
    src: 'https://www.example.com/video.mp4',
    thumbnailUrl: 'https://via.placeholder.com/600x400',
    alt: 'Video',
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    border: createDefaultBorder(),
    padding: createDefaultPadding(),
    responsive: createDefaultResponsive(),
    width: 600,
    height: undefined,
    title: undefined,
    autoPlay: false,
    showControls: true,
    loop: false,
    muted: false,
    provider: 'native',
    ...overrides,
  };
}

export function createDefaultDividerBlockConfiguration(overrides?: Partial<DividerBlockConfiguration>): DividerBlockConfiguration {
  return {
    $blockType: 'divider',
    border: createDefaultBorder({ width: 1, style: 'solid', color: '#CCCCCC' }),
    padding: createDefaultPadding({ top: 20, right: 0, bottom: 20, left: 0 }),
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    responsive: createDefaultResponsive(),
    width: '100%',
    containerWidth: undefined,
    cssClass: undefined,
    ...overrides,
  };
}

export function createDefaultSpacerBlockConfiguration(overrides?: Partial<SpacerBlockConfiguration>): SpacerBlockConfiguration {
  return {
    $blockType: 'spacer',
    height: 20,
    mobileHeight: undefined,
    backgroundColor: 'transparent',
    containerWidth: undefined,
    cssClass: undefined,
    responsive: createDefaultResponsive(),
    ...overrides,
  };
}

export function createDefaultSocialBlockConfiguration(overrides?: Partial<SocialBlockConfiguration>): SocialBlockConfiguration {
  return {
    $blockType: 'social',
    links: [{ platform: 'facebook', url: 'https://facebook.com' }],
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    padding: createDefaultPadding(),
    responsive: createDefaultResponsive(),
    layout: 'horizontal',
    iconSize: 32,
    iconSpacing: 10,
    iconShape: 'circle',
    iconStyle: 'color',
    border: createDefaultBorder(),
    ...overrides,
  };
}

export function createDefaultQuoteBlockConfiguration(overrides?: Partial<QuoteBlockConfiguration>): QuoteBlockConfiguration {
  return {
    $blockType: 'quote',
    text: 'Your quote here...',
    author: undefined,
    source: undefined,
    typography: createDefaultTypography({ fontSize: 18 }),
    alignment: createDefaultAlignment(),
    padding: createDefaultPadding({ top: 20, right: 30, bottom: 20, left: 30 }),
    background: createDefaultBackground(),
    border: createDefaultBorder({ width: 4, color: '#CCCCCC', style: 'solid' }),
    responsive: createDefaultResponsive(),
    quoteMarks: 'none',
    sourceUrl: undefined,
    ...overrides,
  };
}

export function createDefaultMenuBlockConfiguration(overrides?: Partial<MenuBlockConfiguration>): MenuBlockConfiguration {
  return {
    $blockType: 'menu',
    items: [{ text: 'Home', href: 'https://example.com' }],
    typography: createDefaultTypography(),
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    padding: createDefaultPadding(),
    responsive: createDefaultResponsive(),
    layout: 'horizontal',
    itemSpacing: 15,
    separator: undefined,
    hoverColor: '#007bff',
    ...overrides,
  };
}

export function createDefaultSectionBlockConfiguration(overrides?: Partial<SectionBlockConfiguration>): SectionBlockConfiguration {
  return {
    $blockType: 'section',
    background: createDefaultBackground({ color: '#ffffff' }),
    padding: createDefaultPadding({ top: 0, right: 0, bottom: 0, left: 0 }),
    border: createDefaultBorder(),
    responsive: createDefaultResponsive(),
    direction: 'ltr',
    verticalAlign: 'top',
    maxWidth: 600,
    fullWidth: false,
    cssClass: undefined,
    ...overrides,
  };
}

export function createDefaultColumnBlockConfiguration(overrides?: Partial<ColumnBlockConfiguration>): ColumnBlockConfiguration {
  return {
    $blockType: 'column',
    background: createDefaultBackground({ color: '#ffffff' }),
    padding: createDefaultPadding({ top: 10, right: 10, bottom: 10, left: 10 }),
    border: createDefaultBorder(),
    responsive: createDefaultResponsive(),
    width: 'auto',
    verticalAlign: 'top',
    direction: 'ltr',
    cssClass: undefined,
    mobileStack: true,
    mobileStackOrder: 0,
    ...overrides,
  };
}

export function createDefaultGroupBlockConfiguration(overrides?: Partial<GroupBlockConfiguration>): GroupBlockConfiguration {
  return {
    $blockType: 'group',
    background: createDefaultBackground(),
    padding: createDefaultPadding(),
    border: createDefaultBorder(),
    responsive: createDefaultResponsive(),
    direction: 'ltr',
    layout: 'vertical',
    gap: 0,
    itemsAlign: 'stretch',
    cssClass: undefined,
    ...overrides,
  };
}

export function createDefaultHeroBlockConfiguration(overrides?: Partial<HeroBlockConfiguration>): HeroBlockConfiguration {
  return {
    $blockType: 'hero',
    title: undefined,
    subtitle: undefined,
    background: createDefaultBackground({ color: '#1a1a2e' }),
    padding: createDefaultPadding({ top: 40, right: 20, bottom: 40, left: 20 }),
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    titleTypography: createDefaultTypography({ fontSize: 32, fontWeight: 'bold', color: '#ffffff' }),
    subtitleTypography: createDefaultTypography({ fontSize: 16, color: '#cccccc' }),
    border: createDefaultBorder(),
    responsive: createDefaultResponsive(),
    minHeight: 400,
    verticalAlign: 'middle',
    ctaText: undefined,
    ctaUrl: undefined,
    ctaBackgroundColor: '#007bff',
    ctaTextColor: '#ffffff',
    overlayColor: undefined,
    overlayOpacity: 0.4,
    ...overrides,
  };
}

export function createDefaultFooterBlockConfiguration(overrides?: Partial<FooterBlockConfiguration>): FooterBlockConfiguration {
  return {
    $blockType: 'footer',
    companyName: undefined,
    address: undefined,
    legalText: undefined,
    unsubscribeUrl: undefined,
    background: createDefaultBackground({ color: '#F5F5F5' }),
    padding: createDefaultPadding({ top: 30, right: 20, bottom: 30, left: 20 }),
    alignment: createDefaultAlignment({ horizontal: 'center' }),
    typography: createDefaultTypography({ fontSize: 12, color: '#666666' }),
    border: createDefaultBorder(),
    responsive: createDefaultResponsive(),
    links: undefined,
    socialLinks: undefined,
    showUnsubscribe: true,
    cssClass: undefined,
    ...overrides,
  };
}

export function createDefaultHtmlBlockConfiguration(overrides?: Partial<HtmlBlockConfiguration>): HtmlBlockConfiguration {
  return {
    $blockType: 'html',
    content: '',
    responsive: createDefaultResponsive(),
    ...overrides,
  };
}

export function createDefaultAccordionBlockConfiguration(overrides?: Partial<AccordionBlockConfiguration>): AccordionBlockConfiguration {
  return {
    $blockType: 'accordion',
    items: [{ header: 'Section', content: 'Content goes here', isOpen: false }],
    padding: createDefaultPadding(),
    background: createDefaultBackground(),
    border: createDefaultBorder(),
    headerTypography: createDefaultTypography({ fontSize: 16, fontWeight: 'bold' }),
    contentTypography: createDefaultTypography({ fontSize: 14 }),
    responsive: createDefaultResponsive(),
    allowMultipleOpen: false,
    defaultOpenIndex: -1,
    headerBackgroundColor: '#F5F5F5',
    headerActiveColor: '#007bff',
    cssClass: undefined,
    ...overrides,
  };
}

export function createDefaultCarouselBlockConfiguration(overrides?: Partial<CarouselBlockConfiguration>): CarouselBlockConfiguration {
  return {
    $blockType: 'carousel',
    slides: [
      { imageUrl: 'https://via.placeholder.com/600x400', alt: 'Slide 1' },
      { imageUrl: 'https://via.placeholder.com/600x400', alt: 'Slide 2' },
    ],
    padding: createDefaultPadding(),
    background: createDefaultBackground(),
    border: createDefaultBorder(),
    responsive: createDefaultResponsive(),
    width: 600,
    height: 400,
    autoPlay: false,
    autoPlayInterval: 3000,
    showControls: true,
    showIndicators: true,
    loop: true,
    transition: 'slide',
    cssClass: undefined,
    ...overrides,
  };
}

export function createDefaultCustomBlockConfiguration(overrides?: Partial<CustomBlockConfiguration>): CustomBlockConfiguration {
  return {
    $blockType: 'custom',
    customBlockType: '',
    data: {},
    ...overrides,
  };
}
